import React, { useEffect, useState } from "react";
import { BellRing, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { enablePush, isPushSupported, registerPushWorker } from "@/lib/push";

/**
 * Iteration 250 — "Enable Instant Sale Alerts" retrofit banner.
 *
 * Shown on the Parent Dashboard to parents who haven't enabled Web Push
 * yet (including existing Home Screen PWA users). Hides itself when
 * notifications are already granted/subscribed, when the browser can't
 * do push, or when the parent dismissed it (remembered in localStorage).
 */
const DISMISS_KEY = "mttApp:push-banner-dismissed";

export default function PushEnableBanner() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        if (!isPushSupported()) return;
        if (localStorage.getItem(DISMISS_KEY) === "1") return;
        if (Notification.permission !== "default") return;
        const { data } = await api.get("/push/status");
        if (!cancel && data?.push_configured && !data?.subscribed) setVisible(true);
      } catch { /* unsupported / signed-out — stay hidden */ }
    })();
    return () => { cancel = true; };
  }, []);

  if (!visible) return null;

  const enable = async () => {
    setBusy(true);
    try {
      await registerPushWorker();
      await enablePush();
      toast.success("Instant alerts on — you'll get sale, approval and shipping notifications on this device.");
      setVisible(false);
    } catch (e) {
      toast.error(e?.message || "Couldn't enable notifications on this browser.");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* private mode */ }
    setVisible(false);
  };

  return (
    <div
      className="card-base p-4 mb-6 bg-gradient-to-r from-brandPink/10 to-brandTeal/10 border-brandPink/30 flex items-start gap-3"
      data-testid="push-enable-banner"
    >
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-brandPink text-white flex-shrink-0">
        <BellRing className="w-4 h-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-display font-semibold text-brandSlate text-sm">Enable Instant Sale Alerts</div>
        <p className="text-xs text-slate-600 mt-0.5">
          Get a lock-screen ping the moment an item sells, a kid listing needs approval, or an order ships — even when the app is closed.
        </p>
        <button
          type="button"
          onClick={enable}
          disabled={busy}
          className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brandPink text-white text-xs font-bold shadow-sm hover:opacity-90 transition disabled:opacity-60"
          data-testid="push-enable-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />}
          Enable alerts
        </button>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss notifications banner"
        className="text-slate-400 hover:text-slate-700 transition"
        data-testid="push-enable-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
